// Core data types for the application

export interface ModelResponse {
  modelId: string;
  modelName: string;
  provider: string;
  responseText: string;
  status: 'streaming' | 'complete' | 'timeout' | 'error' | 'disabled';
  statusText?: string; // e.g. "Searching the web...", "Searching X..."
  metrics?: ModelMetrics;
  errorMessage?: string;
  isGrok?: boolean;
}

export interface ModelMetrics {
  timeToFirstToken?: number; // milliseconds
  totalTokens?: number;
  responseTime?: number; // milliseconds
}

export interface DemoRun {
  demoRunId: string;
  testId: number;
  userId: string;
  timestamp: number;
  status: 'queued' | 'processing' | 'complete' | 'failed';
  queuePosition?: number;
  responses: Record<string, ModelResponse>;
  round2Ratings?: Record<string, Record<string, { rating: number; rationale: string }>>;
}

// Message Envelope types (discriminated union)
export interface MessageEnvelopeBase {
  demoRunId: string;
  timestamp: string; // ISO 8601
}

export interface TokenMessage extends MessageEnvelopeBase {
  type: 'token';
  payload: { modelId: string; text: string };
}

export interface CompleteMessage extends MessageEnvelopeBase {
  type: 'complete';
  payload: { modelId: string; totalTokens: number; latencyMs: number };
}

export interface ErrorMessage extends MessageEnvelopeBase {
  type: 'error';
  payload: { modelId: string; errorCode: string; message: string };
}

export interface MetricsMessage extends MessageEnvelopeBase {
  type: 'metrics';
  payload: { modelId: string; metrics: ModelMetrics };
}

export interface QueueUpdateMessage extends MessageEnvelopeBase {
  type: 'queue_update';
  payload: { queuePosition: number };
}

export interface StatusMessage extends MessageEnvelopeBase {
  type: 'status';
  payload: { modelId: string; status: string };
}

export type WebSocketMessage =
  | TokenMessage
  | CompleteMessage
  | ErrorMessage
  | MetricsMessage
  | QueueUpdateMessage
  | StatusMessage;

export interface ModelConfig {
  modelId: string;
  provider: string;
  displayName: string;
  enabled: boolean;
}

export interface HistorySummary {
  demoRunId: string;
  testId: number;
  timestamp: number;
  models: string[];
}

export interface User {
  userId: string;
  email: string;
  isAdmin: boolean;
  token: string;
}

const VALID_MESSAGE_TYPES = ['token', 'metrics', 'complete', 'error', 'queue_update', 'status'] as const;

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasBaseFields(obj: Record<string, unknown>): boolean {
  return (
    typeof obj.type === 'string' &&
    typeof obj.demoRunId === 'string' &&
    typeof obj.timestamp === 'string' &&
    isObject(obj.payload)
  );
}

function isValidPayload(type: string, payload: Record<string, unknown>): boolean {
  switch (type) {
    case 'token':
      return typeof payload.modelId === 'string' && typeof payload.text === 'string';
    case 'complete':
      return (
        typeof payload.modelId === 'string' &&
        typeof payload.totalTokens === 'number' &&
        typeof payload.latencyMs === 'number'
      );
    case 'error':
      return (
        typeof payload.modelId === 'string' &&
        typeof payload.errorCode === 'string' &&
        typeof payload.message === 'string'
      );
    case 'metrics':
      return typeof payload.modelId === 'string' && isObject(payload.metrics);
    case 'queue_update':
      return typeof payload.queuePosition === 'number';
    case 'status':
      return typeof payload.modelId === 'string' && typeof payload.status === 'string';
    default:
      return false;
  }
}

/**
 * Validates unknown input and returns a typed WebSocketMessage, or null if invalid.
 */
export function parseWebSocketMessage(data: unknown): WebSocketMessage | null {
  if (!isObject(data)) return null;
  if (!hasBaseFields(data)) return null;

  const type = data.type as string;
  if (!VALID_MESSAGE_TYPES.includes(type as (typeof VALID_MESSAGE_TYPES)[number])) return null;

  const payload = data.payload as Record<string, unknown>;
  if (!isValidPayload(type, payload)) return null;

  return data as unknown as WebSocketMessage;
}

/**
 * Serializes a WebSocketMessage to a JSON string. Useful for tests.
 */
export function serializeMessageEnvelope(msg: WebSocketMessage): string {
  return JSON.stringify(msg);
}

// Analytics Dashboard types

export interface ModelMetricsAggregated {
  modelId: string;
  displayName: string;
  provider: string;
  avgTokens: number;
  avgResponseTime: number;
  avgTimeToFirstToken: number;
  avgRatingReceived: number;
  successRate: number;
  runCount: number;
}

/** Append * to non-bedrock model names */
export function labelModel(m: ModelMetricsAggregated): string {
  return m.provider === 'bedrock' ? m.displayName : `${m.displayName} *`;
}

export interface TestScenarioAnalytics {
  testId: number;
  testName: string;
  totalRuns: number;
  models: Record<string, ModelMetricsAggregated>;
  ratings: Record<string, Record<string, number>>; // rater -> rated -> avg score
}

export interface OverallAnalytics {
  models: Record<string, ModelMetricsAggregated>;
  ratings: Record<string, Record<string, number>>;
  totalRuns: number;
}

export interface AnalyticsResponse {
  testScenarios: Record<string, TestScenarioAnalytics>;
  overall: OverallAnalytics;
}

export interface JudgeCriteria {
  testId: number;
  testName: string;
  criteria: string;
}
